import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Collapse
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PrintIcon from '@mui/icons-material/Print';
import { useSnackbar } from 'notistack';
import cuentaCorrienteProveedoresService, {
  CuentaCorrienteProveedorComprobante,
  CuentaCorrienteProveedorSaldo,
  MovimientoHistoricoProveedor
} from '../../services/cuentaCorrienteProveedoresService';
import proveedorService from '../../services/proveedorService';
import { formatCurrency } from '../../utils/formatters';
import * as XLSX from 'xlsx';
import ProveedorSaldoSearch from '../../components/compras/ProveedorSaldoSearch';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import api from '../../services/api';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import InformesBackButton from '../../components/shared/InformesBackButton';

const formatterFecha = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short'
});

/** Quita el prefijo redundante (FACTURA, RECIBO, ORDEN DE PAGO, NOTA DE CRÉDITO, etc.) del número de comprobante */
const numeroSinPrefijo = (numero: string | null | undefined): string => {
  if (!numero || !numero.trim()) return '-';
  const s = numero.trim();
  const match = s.match(/^(FACTURA|RECIBO|ORDEN\s+DE\s+PAGO|NOTA\s+DE\s+CR[EÉeé]D[IÍií]TO|NOTA\s+DE\s+D[EÉeé]B[IÍií]TO|DEVOLUCI[OÓoó]N|NCI|NDI|FC|ND|NC|REC|DEV)-/i);
  if (match) return s.slice(match[0].length).trim() || '-';
  return s || '-';
};

const ResumenCuenta: React.FC = () => {
  const navigate = useNavigate();
  const { nroProveedor } = useParams<{ nroProveedor: string }>();
  const { enqueueSnackbar } = useSnackbar();
  
  const [allComprobantes, setAllComprobantes] = useState<CuentaCorrienteProveedorComprobante[]>([]);
  const [proveedorInfo, setProveedorInfo] = useState<CuentaCorrienteProveedorSaldo | null>(null);
  const [nroProveedorInput, setNroProveedorInput] = useState<string>('');
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentTab, setCurrentTab] = useState(0);
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const [searchTermMovimientos, setSearchTermMovimientos] = useState<string>('');
  const [pageMovimientos, setPageMovimientos] = useState(0);
  const [rowsPerPageMovimientos, setRowsPerPageMovimientos] = useState(10);
  const [movimientosHistoricos, setMovimientosHistoricos] = useState<MovimientoHistoricoProveedor[]>([]);
  const [totalCountMovimientos, setTotalCountMovimientos] = useState(0);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [saldoInicialPaginaMovimientos, setSaldoInicialPaginaMovimientos] = useState(0);
  const [saldoInicialPeriodo, setSaldoInicialPeriodo] = useState<number | null>(null);
  const [exportingExcelMovimientos, setExportingExcelMovimientos] = useState(false);
  const [exportingPDFMovimientos, setExportingPDFMovimientos] = useState(false);
  const [showFiltrosMovimientos, setShowFiltrosMovimientos] = useState(false);
  const [openAplicarSaldoDialog, setOpenAplicarSaldoDialog] = useState(false);
  const [nroFormularioCreditoAplicar, setNroFormularioCreditoAplicar] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOrdenPagoId, setPdfOrdenPagoId] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const handleCloseAplicarSaldoDialog = () => {
    setOpenAplicarSaldoDialog(false);
    setNroFormularioCreditoAplicar(null);
  };

  const handleImprimirPDFOrdenPago = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/Tesoreria/ordendepago/${nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const cd = (response.headers as { 'content-disposition'?: string })?.['content-disposition'];
      setPdfFileName(getFileNameFromContentDisposition(cd));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setPdfOrdenPagoId(nroFormulario);
    } catch {
      enqueueSnackbar('Error al generar el PDF de la orden de pago', { variant: 'error' });
    }
  };

  const handleClosePDFOrdenPago = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfOrdenPagoId(null);
    setPdfFileName(null);
  };

  const getComprobantePath = (item: CuentaCorrienteProveedorComprobante): string | null => {
    const nroFormulario = item.nroFormulario != null && Number(item.nroFormulario) > 0
      ? Number(item.nroFormulario)
      : null;

    if (!nroFormulario) return null;

    const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
    const comprobanteTxt = (item.comprobante ?? '').toString().toUpperCase();
    const numeroTxt = (item.numero ?? '').toString().toUpperCase();
    const textoDocumento = `${comprobanteTxt} ${numeroTxt}`;

    const esFactura = nroTipo === '1' || textoDocumento.includes('FACTURA') || textoDocumento.includes(' FC');
    if (esFactura) return `/compras/facturas/${nroFormulario}`;

    const esNota =
      nroTipo === '2' ||
      nroTipo === '3' ||
      textoDocumento.includes('NOTA') ||
      textoDocumento.includes('NC') ||
      textoDocumento.includes('ND') ||
      textoDocumento.includes('NCI') ||
      textoDocumento.includes('NDI');
    if (esNota) return `/compras/notas-credito/${nroFormulario}`;

    return null;
  };

  // Función auxiliar para cargar todos los comprobantes en lotes de 50 (límite del backend)
  const loadAllComprobantes = useCallback(async (proveedorNum: number): Promise<CuentaCorrienteProveedorComprobante[]> => {
    const maxPageSize = 50; // Límite del backend
    const allComprobantes: CuentaCorrienteProveedorComprobante[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getComprobantesProveedor(proveedorNum, {
        pageNumber: currentPage,
        pageSize: maxPageSize
      });

      allComprobantes.push(...response.items);

      // Si recibimos menos items que el pageSize, no hay más páginas
      if (response.items.length < maxPageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return allComprobantes;
  }, []);

  const loadData = useCallback(async (proveedorNum: number) => {
    try {
      setLoading(true);
      
      // Cargar información del proveedor
      const saldosResponse = await cuentaCorrienteProveedoresService.getSaldos({
        pageNumber: 1,
        pageSize: 10000
      });
      
      let proveedorInfoData: CuentaCorrienteProveedorSaldo | null =
        saldosResponse.items.find((item) => item.nroProveedor === proveedorNum) ?? null;

      if (!proveedorInfoData) {
        try {
          const prov = await proveedorService.getProveedor(proveedorNum);
          proveedorInfoData = {
            nroProveedor: prov.nroProveedor,
            nombre: prov.nombre || '',
            saldoTotal: 0,
            saldoVencido: 0,
            saldoVigente: 0,
            ultimaActualizacion: ''
          };
        } catch {
          enqueueSnackbar('Proveedor no encontrado.', { variant: 'error' });
          setProveedorInfo(null);
          setAllComprobantes([]);
          return;
        }
      }

      setProveedorInfo(proveedorInfoData);
      setNroProveedorInput(proveedorNum.toString());

      // Cargar todos los comprobantes en lotes de 50 (límite del backend)
      const comprobantes = await loadAllComprobantes(proveedorNum);

      // Ordenar por fecha de emisión (ascendente) y luego por número de comprobante
      const comprobantesOrdenados = [...comprobantes].sort((a, b) => {
        const fechaA = a.fecha ? new Date(a.fecha).getTime() : 0;
        const fechaB = b.fecha ? new Date(b.fecha).getTime() : 0;
        
        // Primero por fecha
        if (fechaA !== fechaB) {
          return fechaA - fechaB;
        }
        
        // Si las fechas son iguales, ordenar por número de comprobante
        const numeroA = a.numero || '';
        const numeroB = b.numero || '';
        return numeroA.localeCompare(numeroB);
      });

      setAllComprobantes(comprobantesOrdenados);
      setPage(0); // Resetear página al cargar nuevo proveedor
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      enqueueSnackbar('No se pudieron cargar los datos.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, loadAllComprobantes]);

  useEffect(() => {
    if (nroProveedor) {
      loadData(Number(nroProveedor));
    }
  }, [nroProveedor, loadData]);

  const loadMovimientosHistoricos = useCallback(async () => {
    if (!nroProveedor || !proveedorInfo) return;
    try {
      setLoadingMovimientos(true);
      const limitedPageSize = Math.min(rowsPerPageMovimientos, 50);
      const response = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
        Number(nroProveedor),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: pageMovimientos + 1,
          pageSize: limitedPageSize
        }
      );
      setMovimientosHistoricos(response.items);
      setTotalCountMovimientos(response.totalCount);
      setSaldoInicialPeriodo(response.saldoInicial ?? null);

      let saldoInicial = response.saldoInicial ?? 0;
      if (pageMovimientos > 0) {
        try {
          const totalAnteriores = pageMovimientos * limitedPageSize;
          const maxPageSize = 50;
          for (let offset = 0; offset < totalAnteriores; offset += maxPageSize) {
            const currentPageSize = Math.min(maxPageSize, totalAnteriores - offset);
            const currentPageNumber = Math.floor(offset / maxPageSize) + 1;
            const lote = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
              Number(nroProveedor),
              {
                fechaDesde: fechaDesde || undefined,
                fechaHasta: fechaHasta || undefined,
                searchTerm: searchTermMovimientos.trim() || undefined,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize
              }
            );
            for (const item of lote.items) {
              saldoInicial += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
            }
          }
        } catch (err) {
          console.warn('No se pudo calcular el saldo inicial de la página:', err);
        }
      }
      setSaldoInicialPaginaMovimientos(saldoInicial);
    } catch (error) {
      console.error('Error al cargar movimientos históricos:', error);
      enqueueSnackbar('No se pudieron cargar los movimientos históricos.', { variant: 'error' });
      setMovimientosHistoricos([]);
      setTotalCountMovimientos(0);
      setSaldoInicialPaginaMovimientos(0);
      setSaldoInicialPeriodo(null);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [
    nroProveedor,
    proveedorInfo,
    fechaDesde,
    fechaHasta,
    searchTermMovimientos,
    pageMovimientos,
    rowsPerPageMovimientos,
    enqueueSnackbar
  ]);

  useEffect(() => {
    if (nroProveedor && proveedorInfo && currentTab === 1) {
      loadMovimientosHistoricos();
    }
  }, [nroProveedor, proveedorInfo, currentTab, loadMovimientosHistoricos]);

  const loadAllMovimientosHistoricos = useCallback(async (): Promise<{
    items: MovimientoHistoricoProveedor[];
    saldoInicial: number | null;
  }> => {
    const maxPageSize = 50;
    const allItems: MovimientoHistoricoProveedor[] = [];
    let saldoInicial: number | null = null;
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteProveedoresService.getMovimientosHistoricosProveedor(
        Number(nroProveedor!),
        {
          fechaDesde: fechaDesde || undefined,
          fechaHasta: fechaHasta || undefined,
          searchTerm: searchTermMovimientos.trim() || undefined,
          pageNumber: currentPage,
          pageSize: maxPageSize
        }
      );
      allItems.push(...response.items);
      if (currentPage === 1 && response.saldoInicial !== undefined) {
        saldoInicial = response.saldoInicial;
      }
      if (response.items.length < maxPageSize) hasMore = false;
      else currentPage++;
    }
    return { items: allItems, saldoInicial };
  }, [nroProveedor, fechaDesde, fechaHasta, searchTermMovimientos]);

  const handleProveedorSelect = (proveedor: CuentaCorrienteProveedorSaldo) => {
    navigate(`/compras/informes/resumen-cuenta/${proveedor.nroProveedor}`);
  };

  const handleProveedorSearch = async () => {
    const nroProveedorNum = Number(nroProveedorInput);
    if (!nroProveedorNum || isNaN(nroProveedorNum)) {
      enqueueSnackbar('Ingrese un número de proveedor válido.', { variant: 'warning' });
      return;
    }
    navigate(`/compras/informes/resumen-cuenta/${nroProveedorNum}`);
  };

  // Paginación en el frontend
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const comprobantes = allComprobantes.slice(startIndex, endIndex);
  const totalCount = allComprobantes.length;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageMovimientos = (_: unknown, newPage: number) => {
    setPageMovimientos(newPage);
  };

  const handleChangeRowsPerPageMovimientos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const limited = Math.min(newRowsPerPage, 50);
    setRowsPerPageMovimientos(limited);
    setPageMovimientos(0);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);

      // Calcular saldo acumulado usando todos los comprobantes
      let saldoAcumulado = 0;
      const data = allComprobantes.map((item) => {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        return {
          'Fecha': item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          'Comprobante': item.comprobante,
          'Número': numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          'Importe': item.importe,
          'Debe': item.saldoDeb,
          'Haber': item.saldoHab,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Vencimiento': item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '',
          'Nro. Formulario': item.nroFormulario
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ResumenCuenta');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `resumen_cuenta_proveedor_${nroProveedor}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (totalCount === 0 || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);

      if (!allComprobantes.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumen de Cuenta - Cuenta Corriente Proveedores', marginX, currentY);
      currentY += 24;

      // Información del proveedor
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Proveedor: ${nroProveedor} - ${proveedorInfo.nombre}`, marginX, currentY);
      currentY += 20;

      // Calcular saldo acumulado para la tabla usando todos los comprobantes
      let saldoAcumulado = 0;
      const tableData = allComprobantes.map((item) => {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        return [
          item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          item.comprobante || '',
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          formatCurrency(item.importe),
          formatCurrency(item.saldoDeb),
          formatCurrency(item.saldoHab),
          formatCurrency(saldoAcumulado),
          item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : ''
        ];
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha', 'Comprobante', 'Número', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
          3: { halign: 'right', cellWidth: 70 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 50 },
          6: { halign: 'right', cellWidth: 75 },
          7: { cellWidth: 70 }
        },
        tableWidth: 'auto'
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(`Saldo Cuenta Corriente: ${formatCurrency(proveedorInfo.saldoTotal)}`, marginX, currentY);

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `resumen_cuenta_proveedor_${nroProveedor}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportExcelMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroProveedor || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingExcelMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const data: Record<string, string | number>[] = [];
      if (fechaDesde && saldoInicial !== null) {
        data.push({
          'Fecha Emisión': `Saldo Inicial al ${formatterFecha.format(new Date(fechaDesde))}`,
          'Comprobante': '',
          'Número': '',
          'Descripción': '',
          'Importe Total': '',
          'Debe': '',
          'Haber': '',
          'Saldo Acumulado': saldoInicial,
          'Fecha Venc.': ''
        });
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        data.push({
          'Fecha Emisión': item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          'Comprobante': item.descripcionTip || '',
          'Número': numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          'Descripción': item.descripcionFor || '',
          'Importe Total': item.importeTotal ?? 0,
          'Debe': item.impCompDebe ?? 0,
          'Haber': item.impCompHaber ?? 0,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Venc.': item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : ''
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'MovimientosHistoricos');
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `movimientos_historicos_proveedor_${nroProveedor}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcelMovimientos(false);
    }
  };

  const handleExportPDFMovimientos = async () => {
    if (totalCountMovimientos === 0 || !nroProveedor || !proveedorInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }
    try {
      setExportingPDFMovimientos(true);
      const { items, saldoInicial } = await loadAllMovimientosHistoricos();
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Movimientos Históricos - Cuenta Corriente Proveedores', marginX, currentY);
      currentY += 24;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Proveedor: ${nroProveedor} - ${proveedorInfo.nombre}`, marginX, currentY);
      currentY += 16;
      if (fechaDesde || fechaHasta) {
        doc.text(
          `Período: ${fechaDesde ? formatterFecha.format(new Date(fechaDesde)) : '...'} - ${fechaHasta ? formatterFecha.format(new Date(fechaHasta)) : '...'}`,
          marginX,
          currentY
        );
        currentY += 16;
      }
      currentY += 8;

      const tableData: (string | number)[][] = [];
      if (fechaDesde && saldoInicial !== null) {
        tableData.push([
          `Saldo Inicial al ${formatterFecha.format(new Date(fechaDesde))}`,
          '',
          '',
          '',
          '',
          '',
          '',
          formatCurrency(saldoInicial),
          ''
        ]);
      }
      let saldoAcumulado = saldoInicial ?? 0;
      items.forEach((item) => {
        saldoAcumulado += (item.impCompDebe ?? 0) - (item.impCompHaber ?? 0);
        tableData.push([
          item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '',
          item.descripcionTip || '',
          numeroSinPrefijo(item.numero) === '-' ? '' : numeroSinPrefijo(item.numero),
          item.descripcionFor || '',
          formatCurrency(item.importeTotal ?? 0),
          formatCurrency(item.impCompDebe ?? 0),
          formatCurrency(item.impCompHaber ?? 0),
          formatCurrency(saldoAcumulado),
          item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : ''
        ]);
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha Emisión', 'Comprobante', 'Número', 'Descripción', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 45 },
          2: { cellWidth: 55 },
          3: { cellWidth: 65 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 40 },
          6: { halign: 'right', cellWidth: 40 },
          7: { halign: 'right', cellWidth: 60 },
          8: { cellWidth: 50 }
        },
        tableWidth: 'auto'
      });
      currentY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? currentY;
      currentY += 16;
      doc.setFont('helvetica', 'bold');
      doc.text(`Saldo Cuenta Corriente: ${formatCurrency(proveedorInfo.saldoTotal)}`, marginX, currentY);

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_historicos_proveedor_${nroProveedor}_${today}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDFMovimientos(false);
    }
  };

  // Calcular saldo acumulado para mostrar en la tabla
  const calcularSaldoAcumulado = (indexInPage: number): number => {
    const globalIndex = startIndex + indexInPage;
    let saldo = 0;
    for (let i = 0; i <= globalIndex; i++) {
      if (allComprobantes[i]) {
        saldo += allComprobantes[i].saldoDeb - allComprobantes[i].saldoHab;
      }
    }
    return saldo;
  };

  const calcularSaldoAcumuladoMovimientos = (indexInPage: number): number => {
    let saldo = saldoInicialPaginaMovimientos;
    for (let i = 0; i <= indexInPage; i++) {
      if (movimientosHistoricos[i]) {
        saldo += (movimientosHistoricos[i].impCompDebe ?? 0) - (movimientosHistoricos[i].impCompHaber ?? 0);
      }
    }
    return saldo;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 3 }}
          >
            <InformesBackButton to="/compras/informes" useBrowserBack />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Resumen de Cuenta Corriente de Proveedores
            </Typography>
          </Stack>

          {/* Formulario de búsqueda */}
          <Card 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              bgcolor: '#fafafa'
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Número de Proveedor"
                    value={nroProveedorInput}
                    onChange={(e) => setNroProveedorInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleProveedorSearch();
                      }
                    }}
                    helperText="Escriba el número para buscar automáticamente"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => setOpenProveedorSearch(true)} 
                            edge="end" 
                            color="primary" 
                            title="Seleccionar proveedor"
                            sx={{
                              borderRadius: 1.5,
                            }}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                {proveedorInfo && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={proveedorInfo.nombre}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Total"
                        value={formatCurrency(proveedorInfo.saldoTotal)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: '#1976d2',
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Vencido"
                        value={formatCurrency(proveedorInfo.saldoVencido ?? 0)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: proveedorInfo.saldoVencido && proveedorInfo.saldoVencido > 0 ? '#d32f2f' : 'inherit',
                            }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          {proveedorInfo && (
            <Tabs
              value={currentTab}
              onChange={(_, v: number) => setCurrentTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Composición de Saldo" />
              <Tab label="Movimientos Históricos" />
            </Tabs>
          )}

          {/* Mensaje cuando no hay proveedor seleccionado */}
          {!proveedorInfo && !loading && (
            <Card 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                bgcolor: '#fafafa'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Seleccione un proveedor para ver la composición de su saldo
              </Typography>
            </Card>
          )}

          {/* Tab 0: Composición de Saldo */}
          {proveedorInfo && currentTab === 0 && (
            <>
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AccountBalanceWalletIcon />}
                    onClick={() => {
                      setNroFormularioCreditoAplicar(null);
                      setOpenAplicarSaldoDialog(true);
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Aplicar saldos pendientes
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={loading || exportingExcel || comprobantes.length === 0}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPDF}
                    disabled={loading || exportingPDF || comprobantes.length === 0}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)',
                      }
                    }}
                  >
                    {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                  </Button>
                </Stack>
              </Box>
              <TableContainer 
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                }}
              >
                {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <CircularProgress />
                </Box>
              ) : comprobantes.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <Typography>No hay comprobantes para este proveedor.</Typography>
                </Box>
              ) : (
                <>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comprobantes.map((item, index) => {
                        const saldoAcumulado = calcularSaldoAcumulado(index);
                        const nroTipo = (item.nroTipoFormulario ?? '').toString().trim();
                        const esCreditoAplicable = nroTipo === '6' || nroTipo === '3';
                        const nroFormOk =
                          item.nroFormulario != null && Number(item.nroFormulario) > 0
                            ? Number(item.nroFormulario)
                            : null;
                        const comprobantePath = getComprobantePath(item);
                        return (
                          <TableRow 
                            key={`${item.numero}-${index}`}
                            sx={{
                              '&:hover': {
                                bgcolor: '#f9f9f9',
                              },
                            }}
                          >
                            <TableCell>
                              {item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-'}
                            </TableCell>
                            <TableCell>{item.comprobante || '-'}</TableCell>
                            <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoDeb)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoHab)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoAcumulado)}
                            </TableCell>
                            <TableCell
                              sx={
                                item.fechaVenc && new Date(item.fechaVenc) < new Date()
                                  ? { color: '#d32f2f', fontWeight: 600 }
                                  : {}
                              }
                            >
                              {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {esCreditoAplicable && nroFormOk ? (
                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                  {comprobantePath && (
                                    <Tooltip title="Visualizar documento">
                                      <IconButton
                                        size="small"
                                        onClick={() => navigate(comprobantePath)}
                                        sx={{
                                          bgcolor: '#0284c7',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          '&:hover': { bgcolor: '#0369a1', transform: 'scale(1.05)' },
                                          transition: 'all 0.2s',
                                        }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {nroTipo === '6' && (
                                    <Tooltip title="Imprimir PDF">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleImprimirPDFOrdenPago(nroFormOk)}
                                        sx={{
                                          bgcolor: '#2563eb',
                                          color: '#fff',
                                          borderRadius: 1.5,
                                          '&:hover': { bgcolor: '#1d4ed8', transform: 'scale(1.05)' },
                                          transition: 'all 0.2s',
                                        }}
                                      >
                                        <PrintIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Aplicar saldo / Ver aplicaciones">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setNroFormularioCreditoAplicar(nroFormOk);
                                        setOpenAplicarSaldoDialog(true);
                                      }}
                                      sx={{
                                        bgcolor: '#15803d',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: '#166534', transform: 'scale(1.05)' },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <AccountBalanceWalletIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                comprobantePath ? (
                                  <Tooltip title="Visualizar documento">
                                    <IconButton
                                      size="small"
                                      onClick={() => navigate(comprobantePath)}
                                      sx={{
                                        bgcolor: '#0284c7',
                                        color: '#fff',
                                        borderRadius: 1.5,
                                        '&:hover': { bgcolor: '#0369a1', transform: 'scale(1.05)' },
                                        transition: 'all 0.2s',
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : '—'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                </>
              )}
            </TableContainer>
            </>
          )}

          {/* Tab 1: Movimientos Históricos */}
          {proveedorInfo && currentTab === 1 && (
            <>
              <Box mb={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<FilterListIcon />}
                    endIcon={showFiltrosMovimientos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFiltrosMovimientos(!showFiltrosMovimientos)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                  >
                    Filtrar
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportExcelMovimientos}
                      disabled={loadingMovimientos || exportingExcelMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {exportingExcelMovimientos ? 'Exportando...' : 'Exportar Excel'}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleExportPDFMovimientos}
                      disabled={loadingMovimientos || exportingPDFMovimientos || movimientosHistoricos.length === 0}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)'
                        }
                      }}
                    >
                      {exportingPDFMovimientos ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </Stack>
                </Box>
                <Collapse in={showFiltrosMovimientos} timeout="auto" unmountOnExit>
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                    <TextField
                      size="small"
                      label="Fecha Desde"
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => { setFechaDesde(e.target.value); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Fecha Hasta"
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => { setFechaHasta(e.target.value); setPageMovimientos(0); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      size="small"
                      label="Buscar"
                      placeholder="Descripción, comprobante..."
                      value={searchTermMovimientos}
                      onChange={(e) => { setSearchTermMovimientos(e.target.value); setPageMovimientos(0); }}
                      onKeyPress={(e) => { if (e.key === 'Enter') loadMovimientosHistoricos(); }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => loadMovimientosHistoricos()}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Buscar
                    </Button>
                  </Box>
                </Collapse>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0'
                }}
              >
                {loadingMovimientos ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <CircularProgress />
                  </Box>
                ) : movimientosHistoricos.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                    <Typography>No hay movimientos para este proveedor con los filtros aplicados.</Typography>
                  </Box>
                ) : (
                  <>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Emisión</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fechaDesde && saldoInicialPeriodo !== null && (
                          <TableRow sx={{ bgcolor: '#f0f7ff', '&:hover': { bgcolor: '#e8f0fe' } }}>
                            <TableCell colSpan={4} sx={{ fontStyle: 'italic' }}>
                              Saldo Inicial al {formatterFecha.format(new Date(fechaDesde))}
                            </TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoInicialPeriodo)}
                            </TableCell>
                            <TableCell>-</TableCell>
                          </TableRow>
                        )}
                        {movimientosHistoricos.map((item, index) => {
                          const saldoAcumulado = calcularSaldoAcumuladoMovimientos(index);
                          return (
                            <TableRow
                              key={`${item.numero}-${item.fechaEmision}-${index}`}
                              sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                            >
                              <TableCell>
                                {item.fechaEmision ? formatterFecha.format(new Date(item.fechaEmision)) : '-'}
                              </TableCell>
                              <TableCell>{item.descripcionTip || '-'}</TableCell>
                              <TableCell>{numeroSinPrefijo(item.numero)}</TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>{item.descripcionFor || '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(item.importeTotal ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.impCompDebe ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.impCompHaber ?? 0)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                {formatCurrency(saldoAcumulado)}
                              </TableCell>
                              <TableCell
                                sx={
                                  item.fechaVencim && new Date(item.fechaVencim) < new Date()
                                    ? { color: '#d32f2f', fontWeight: 600 }
                                    : {}
                                }
                              >
                                {item.fechaVencim ? formatterFecha.format(new Date(item.fechaVencim)) : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={totalCountMovimientos}
                      rowsPerPage={rowsPerPageMovimientos}
                      page={pageMovimientos}
                      onPageChange={handleChangePageMovimientos}
                      onRowsPerPageChange={handleChangeRowsPerPageMovimientos}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                      sx={{ borderTop: '1px solid #e0e0e0' }}
                    />
                  </>
                )}
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <ProveedorSaldoSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      {pdfUrl && pdfOrdenPagoId != null && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDFOrdenPago}
          title={`Orden de pago ${pdfOrdenPagoId}`}
          documentId={`${pdfOrdenPagoId}`}
          documentType="ordenPago"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}

      {proveedorInfo && nroProveedor && (
        <AplicarSaldoReciboDialog
          open={openAplicarSaldoDialog}
          onClose={handleCloseAplicarSaldoDialog}
          variant="proveedor"
          nroCliente={Number(nroProveedor)}
          nombreCliente={proveedorInfo.nombre || `Proveedor ${nroProveedor}`}
          reciboId={nroFormularioCreditoAplicar ?? undefined}
          selectorMode={nroFormularioCreditoAplicar == null}
          onSuccess={() => {
            loadData(Number(nroProveedor));
            if (currentTab === 1) loadMovimientosHistoricos();
          }}
        />
      )}
    </Box>
  );
};

export default ResumenCuenta;



